import React from "react";

import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";

export type AccountMetricsRowProps = {
  name: string;
  initials: string;
  tier?: string;
  rightActions?: React.ReactNode;
  // Watch list account fields
  industry?: string;
  location?: string;
  primarySponsors?: string;
  secondarySponsors?: string;
  revenue?: string;
  renewal?: string;
  // Legacy fields for backward compatibility
  level?: string;
  sponsorScore?: string | number;
  sponsorStartDate?: string;
  arr?: string;
  nrr?: string;
  csat?: string | number;
  nps?: string | number;
  sponsorsCount?: number | string;
};

const AccountMetricsRow: React.FC<AccountMetricsRowProps> = ({
  name,
  initials,
  tier,
  rightActions,
  industry,
  location,
  primarySponsors,
  secondarySponsors,
  revenue,
  renewal,
  // Legacy fields
  level,
  sponsorScore,
  sponsorStartDate,
  arr,
  nrr,
  csat,
  nps,
  sponsorsCount,
}) => {
  const { formatTier } = useAccountTiers();
  const tierLabel = formatTier(tier) || tier;

  const formatScore = (val: string | number | undefined) =>
    val === undefined || val === "" ? "-" : String(val);

  const MetricItem: React.FC<{ label: string; value: string | number }> = ({
    label,
    value,
  }) => (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );

  // Check if this is a watch list account (has new fields)
  const isWatchListAccount =
    industry ||
    location ||
    primarySponsors ||
    secondarySponsors ||
    revenue ||
    renewal;

  return (
    <div className="flex items-center justify-between py-2 transition-all duration-200 hover:bg-gray-50/50 border-b border-gray-100 last:border-b-0 group">
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium primary-color">
              {initials}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {name}
            </h3>
            {level && (
              <span className="inline-flex items-center px-1 py-0.5 rounded-md text-xs font-medium bg-gray-500 text-white whitespace-nowrap shadow-sm">
                {level}
              </span>
            )}
            {tierLabel && (
              <span
                className={`inline-flex items-center px-1 py-0.5 rounded-md text-xs font-medium whitespace-nowrap shadow-sm ${getTierBadgeClasses(tier)}`}
              >
                {tierLabel}
              </span>
            )}
          </div>

          {isWatchListAccount ? (
            // Watch list account format - single line with all fields
            <div className="grid grid-cols-6 gap-x-4 items-center text-sm">
              {industry && <MetricItem label="Industry" value={industry} />}
              {location && <MetricItem label="Location" value={location} />}
              {primarySponsors && (
                <MetricItem label="Primary Sponsors" value={primarySponsors} />
              )}
              {secondarySponsors && (
                <MetricItem
                  label="Secondary Sponsors"
                  value={secondarySponsors}
                />
              )}
              {revenue && <MetricItem label="Revenue" value={revenue} />}
              {renewal && <MetricItem label="Renewal" value={renewal} />}
            </div>
          ) : (
            // Legacy format for regular accounts
            <div className="flex items-center flex-nowrap justify-between w-full gap-x-4 overflow-x-auto">
              {sponsorScore !== undefined && (
                <MetricItem
                  label="Sponsor Score"
                  value={formatScore(sponsorScore)}
                />
              )}
              {sponsorStartDate && (
                <MetricItem label="Start Date" value={sponsorStartDate} />
              )}
              {arr && <MetricItem label="ARR" value={arr} />}
              {nrr && <MetricItem label="NRR" value={nrr} />}
              {csat !== undefined && (
                <MetricItem label="CSAT" value={formatScore(csat)} />
              )}
              {nps !== undefined && (
                <MetricItem label="NPS" value={formatScore(nps)} />
              )}
              {sponsorsCount !== undefined && (
                <MetricItem label="Sponsors" value={sponsorsCount} />
              )}
            </div>
          )}
        </div>
      </div>
      {rightActions && (
        <div className="flex items-center gap-2 ml-6 flex-shrink-0">
          {rightActions}
        </div>
      )}
    </div>
  );
};

export default AccountMetricsRow;
